/**
 * ActionButtons Component
 *
 * Manages action button groups in chat content.
 * Handles click states, mutual exclusion, and emits events to orchestrator.
 */

import { eventBus } from '../core/utils.js';

export class ActionButtons {
  constructor() {
    this.activeButtons = [];  // Registry of current action buttons
    this.init();
  }

  /**
   * Initialize component by setting up event delegation
   */
  init() {
    // Use event delegation on document for dynamic button groups
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.action-btn');
      if (button && !button.disabled && !button.classList.contains('action-btn--clicked')) {
        this.handleButtonClick(button);
      }
    });
  }

  /**
   * Handle button click event
   * Emits event to orchestrator with action data
   *
   * @param {HTMLElement} button - The clicked button element
   */
  handleButtonClick(button) {
    const container = button.closest('.action-buttons');
    if (!container) return;

    // One choice per group: once any button in this container has been
    // clicked, the group is committed. Siblings only fade (`--hiding` keeps
    // them clickable in the DOM) and the input-region teardown runs AFTER the
    // async action handler completes — without this guard a second click
    // during a multi-second navigation handler dispatches a racing duplicate
    // action (e.g. two session creations).
    if (container.querySelector('.action-btn--clicked')) return;

    // Visual feedback - mark button as clicked (blue background)
    button.classList.add('action-btn--clicked');

    // Animate siblings: fade out and collapse (replaces disabled logic)
    // Flexbox with justify-content: flex-end means clicked button slides right as siblings collapse
    const siblingButtons = container.querySelectorAll('.action-btn');
    siblingButtons.forEach(sibling => {
      if (sibling !== button) {
        sibling.classList.add('action-btn--hiding');
      }
    });

    // Also hide any inline action messages
    const actionMessages = container.querySelectorAll('.action-message');
    actionMessages.forEach(message => {
      message.classList.add('action-message--hiding');
    });

    // Scroll clicked button to top of viewport (happens concurrently with fade/slide)
    const messageContainer = container.closest('.message--interactive');
    if (messageContainer && window.modeOrchestrator?.chatManager?.messageList) {
      window.modeOrchestrator.chatManager.messageList.scrollToMessage(messageContainer);
    }

    // Extract action data from button attributes. The label is the text
    // node(s) of `.action-btn__text` only: a `metadata.sublabel` span nests
    // inside that container (see createButton), and its helper copy must not
    // ride along into the echoed bubble or the persisted `echoText`.
    const textEl = button.querySelector('.action-btn__text');
    const labelText = textEl
      ? Array.from(textEl.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join('')
        .trim()
      : '';
    const actionData = {
      action: button.dataset.action,           // e.g., "switch_mode:DISCOVERY"
      actionId: button.dataset.actionId || button.dataset.action || `btn-${Date.now()}`,
      label: labelText || 'Button',
      groupId: container.dataset.groupId,      // Optional: button group ID
      data: JSON.parse(button.dataset.actionData || '{}')  // Additional payload
    };

    // Emit event for orchestrator
    eventBus.emit('action:button-click', {
      actionId: actionData.actionId,
      actionData: actionData
    });

    console.log('[ActionButtons] Button clicked, event emitted:', actionData);
  }

  /**
   * Create a button group programmatically
   * Supports mixed items: buttons and inline messages
   *
   * @param {Array} items - Array of item configuration objects (buttons or messages)
   * @param {Object} items[].type - Item type: omit for button, 'message' for inline text
   * @param {string} items[].label - Button text (for buttons)
   * @param {string} items[].action - Action identifier (for buttons)
   * @param {string} items[].text - Message text (for type: 'message')
   * @param {Object} options - Group options (layout, groupId, etc.)
   * @returns {HTMLElement} The container element with items
   *
   * @example
   * // Mixed buttons and inline message
   * createButtonGroup([
   *   { label: "Skip", action: 'skip_name' },
   *   { type: 'message', text: "Or, enter a name below" }
   * ]);
   */
  createButtonGroup(items, options = {}) {
    const container = document.createElement('div');
    container.className = 'action-buttons';

    // Set group ID if provided (for tracking)
    if (options.groupId) {
      container.dataset.groupId = options.groupId;
    }

    // Separate buttons from messages for registration
    const buttonsOnly = [];

    // Create and append items (buttons or messages)
    items.forEach(config => {
      if (config.type === 'message') {
        // Create inline message element
        const message = this.createInlineMessage(config);
        container.appendChild(message);
      } else {
        // Create button element (default)
        const button = this.createButton(config);
        container.appendChild(button);
        buttonsOnly.push(config);
      }
    });

    // Register only actual buttons as the current active buttons
    this.registerActiveButtons(buttonsOnly);

    return container;
  }

  /**
   * Create an inline message element (non-interactive text alongside buttons)
   *
   * @param {Object} config - Message configuration
   * @param {string} config.text - Message text to display
   * @returns {HTMLElement} The message span element
   */
  createInlineMessage(config) {
    const span = document.createElement('span');
    span.className = 'action-message';
    span.textContent = config.text || '';
    return span;
  }

  /**
   * Create a single button element
   *
   * @param {Object} config - Button configuration
   * @param {string} config.label - Button text (replaces old 'text' property)
   * @param {string} config.action - Action identifier (e.g., "switch_mode:DISCOVERY")
   * @param {string} [config.id] - Unique button ID
   * @param {string} [config.icon] - Icon SVG or emoji
   * @param {Object} [config.data] - Additional data payload
   * @param {Object} [config.metadata] - Metadata (recommended, sublabel, parenthetical, etc.)
   * @returns {HTMLElement} The button element
   */
  createButton(config) {
    const button = document.createElement('button');
    button.className = config.className ? `action-btn ${config.className}` : 'action-btn';

    // Set required data attributes
    button.dataset.action = config.action;
    button.dataset.actionId = config.id || `btn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Set optional data payload
    if (config.data) {
      button.dataset.actionData = JSON.stringify(config.data);
    }

    // Add icon if provided (emoji or SVG)
    if (config.icon) {
      const iconSpan = document.createElement('span');
      iconSpan.className = 'action-btn__icon';
      iconSpan.innerHTML = config.icon; // Supports emoji or SVG
      button.appendChild(iconSpan);
    }

    // Add text (use 'label' property for consistency)
    const textSpan = document.createElement('span');
    textSpan.className = 'action-btn__text';
    textSpan.textContent = config.label || config.text || 'Button';
    button.appendChild(textSpan);

    // Sublabel — one plain-language line under the label stating what the
    // click does to underlying state (e.g. "Archives it — restore anytime").
    // Lives inside .action-btn__text so the in-input row layout (label left,
    // "Suggested" chip right) is preserved.
    if (config.metadata?.sublabel) {
      const subSpan = document.createElement('span');
      subSpan.className = 'action-btn__sublabel';
      subSpan.textContent = config.metadata.sublabel;
      textSpan.appendChild(subSpan);
    }

    // Add metadata (recommended/parenthetical) if provided
    if (config.metadata?.recommended) {
      button.classList.add('recommended');
      // "Suggested" chip — surfaced only for relocated in-input groups via CSS
      // (`.chat-input__buttons .action-btn.recommended .action-btn__tag`); kept
      // hidden for transcript buttons so their styling is unchanged.
      const tagSpan = document.createElement('span');
      tagSpan.className = 'action-btn__tag';
      tagSpan.textContent = 'Suggested';
      button.appendChild(tagSpan);
    } else if (config.metadata?.parenthetical) {
      const metaSpan = document.createElement('span');
      metaSpan.className = 'action-btn__meta action-btn__meta--parenthetical';
      metaSpan.textContent = `(${config.metadata.parenthetical})`;
      button.appendChild(metaSpan);
    }

    return button;
  }

  // ===== Active Button Registry Methods =====

  /**
   * Register buttons as the current active buttons.
   * Called automatically by createButtonGroup().
   * These buttons represent what the user can currently see and interact with.
   *
   * @param {Array} buttons - Array of button configuration objects
   */
  registerActiveButtons(buttons) {
    this.activeButtons = buttons.map(b => ({
      id: b.id || null,
      action: b.action,
      label: b.label || b.text || 'Button'
    }));
  }

  /**
   * Get the currently registered active buttons.
   * Used when sending user messages to include button context.
   *
   * @returns {Array} Array of active button objects with id, action, label
   */
  getActiveButtons() {
    return this.activeButtons;
  }

  /**
   * Clear the active buttons registry.
   * Called when user sends a message (after capturing buttons for the request).
   */
  clearActiveButtons() {
    this.activeButtons = [];
  }

  /**
   * Check if there are any active buttons registered.
   *
   * @returns {boolean} True if active buttons exist
   */
  hasActiveButtons() {
    return this.activeButtons.length > 0;
  }
}
